import { z } from 'zod';

/**
 * The setup sandbox's testing panel (APP-096): Settings › System administration › Preview &
 * testing, in dev only. The working environment is where a reset is asked for and watched; the
 * sandbox says what it is and sends people there. Status comes from the reset controller's
 * journal (INFRA-017 §6), never from the sandbox's database, so it reads the same while the
 * sandbox is being rebuilt.
 */

export const SetupWorkspace = z.enum(['working', 'setup']);
export type SetupWorkspace = z.infer<typeof SetupWorkspace>;

/** A sandbox generation: a whole number the controller counts up at each reset. */
export const SetupGeneration = z.string().regex(/^\d{1,9}$/);

export const SetupOperationState = z.enum(['queued', 'running', 'succeeded', 'failed']);
export type SetupOperationState = z.infer<typeof SetupOperationState>;

/** One reset, as the controller's receipt records it (queued until the controller picks it up). */
export const SetupOperation = z.object({
  operationId: z.uuid(),
  state: SetupOperationState,
  /**
   * The step it's on or stopped at, in plain words ("Removing the old keys"); null from a
   * controller that doesn't say. The controller's machine names never reach the browser.
   */
  progress: z.string().max(80).nullable(),
  oldGeneration: SetupGeneration.nullable(),
  newGeneration: SetupGeneration.nullable(),
  baselineVersion: z.string().max(40).nullable(),
  applicationSha: z.string().max(40).nullable(),
  requestedAt: z.string(),
  finishedAt: z.string().nullable(),
  /** The controller's plain words on the outcome ("…has been reset and is ready", a refusal). */
  message: z.string().max(500).nullable(),
});
export type SetupOperation = z.infer<typeof SetupOperation>;

/** The sandbox now, from the controller's `active.json`. */
export const SetupSandboxState = z.object({
  state: z.enum(['active', 'maintenance', 'failed']),
  generation: SetupGeneration,
  baselineVersion: z.string().max(40).nullable(),
  applicationSha: z.string().max(40).nullable(),
  activatedAt: z.string().nullable(),
  /** The build the sandbox runs now (a release can move it); null when not reported. */
  runningSha: z.string().max(40).nullable(),
  /** Whether that's this environment's build (matched by prefix); null when not reported. */
  runsThisBuild: z.boolean().nullable(),
});
export type SetupSandboxState = z.infer<typeof SetupSandboxState>;

/** Why this person can't ask for a reset here. */
export const SetupResetBlocked = z.enum([
  /** The sandbox's own panel: resets run from the working environment. */
  'from_working',
  /** Not on the deployment's operator list. */
  'not_operator',
  /** The controller's journal can't be read right now. */
  'status_unavailable',
  /** The sandbox hasn't been built yet. */
  'not_built',
  /** A reset is queued or running. */
  'in_progress',
  /** The sandbox runs another build; it moves to this one with the next release. */
  'different_build',
]);
export type SetupResetBlocked = z.infer<typeof SetupResetBlocked>;

export const SetupSandboxPanel = z.object({
  /** Which environment this page is in. */
  workspace: SetupWorkspace,
  origins: z.object({ working: z.url(), setup: z.url() }),
  /** This page's own generation, in the sandbox; null in the working environment. */
  pageGeneration: SetupGeneration.nullable(),
  /** The sandbox now; null when it hasn't been built or its status can't be read. */
  sandbox: SetupSandboxState.nullable(),
  /** False when the controller's journal couldn't be read. */
  statusReadable: z.boolean(),
  /** The latest reset: the one waiting or running, or the one that made (or failed) this one. */
  latest: SetupOperation.nullable(),
  /** This build and the baseline it installs. */
  applicationVersion: z.string(),
  baseline: z.object({ version: z.string(), hash: z.string() }),
  /** Whether the person may ask for a reset here, and if not, why. */
  canReset: z.boolean(),
  resetBlocked: SetupResetBlocked.nullable(),
});
export type SetupSandboxPanel = z.infer<typeof SetupSandboxPanel>;

/** The confirmation the dialog sends: the operator ticked the box naming the setup sandbox. */
export const SETUP_RESET_CONFIRMATION = 'Reset setup sandbox';

export const StartSetupReset = z
  .object({
    /** The generation the person was looking at: a reset never applies to a newer one. */
    expectedGeneration: SetupGeneration,
    reason: z.string().trim().min(3, 'Say why.').max(500),
    confirmation: z.literal(SETUP_RESET_CONFIRMATION),
  })
  .strict();
export type StartSetupReset = z.infer<typeof StartSetupReset>;

export const SetupResetAccepted = z.object({
  operationId: z.uuid(),
  state: SetupOperationState,
});
export type SetupResetAccepted = z.infer<typeof SetupResetAccepted>;
